
import type { AmountString, } from "@bracket/types";



/**
 * Floors a number to a specified precision
 * @param value - The number to floor
 * @param precision - The number of decimal places to floor to (default: 5)
 * @returns The floored number
 * @example
 * floorOnPrecision(1.23456789, 2) // returns 1.23
 * floorOnPrecision(1.23456789, 5) // returns 1.23456
 */
export const floorOnPrecisionString = (value: number|AmountString, precision = 5): AmountString => {
	return Math.floor((Number(value) * (10**precision)) / (10**precision)).toFixed(precision);
}

export const floorOnPrecisionString2 = (value: number|AmountString, precision = 5): AmountString => {
	return (Math.floor(Number(value) * (10**precision)) / (10**precision)).toFixed(5)
}

export const floorOnPrecision = ( value: number|AmountString, precision = 5 ): number => {
	console.warn("floorOnPrecision deprecated - use floorToPrecision instead", value, precision);
	return Math.floor(Number(value) * (10**precision) / (10**precision));
}

export const floorOnPrecisionBigInt = ( value: number|AmountString, precision = 5 ): bigint => {
	return BigInt(Math.floor(Number(value) * (10**precision)))
}


export const subtractAmountString = ( initial: AmountString, sub: AmountString|AmountString[], opts: { precision?: number } = {}): AmountString => {
	const { precision, } = Object.assign({}, subtractAmountStringDefaults, opts);
	const subs = Array.isArray(sub) ? sub : [ sub ];
	return subs.reduce((acc, curr) => {
		return floorOnPrecisionString2(floorOnPrecision(acc) - floorOnPrecision(curr), precision);
	}, initial);
}


/**
 * Floors a number to a specified precision and removes dust values
 * @param value - The number or amount string to floor
 * @param precision - The number of decimal places to floor to (default: 5)
 * @returns The floored number with dust removed
 * @example
 * floorToPrecision(1.23456789, 2) // returns 1.23
 * floorToPrecision(1.23456789, 5) // returns 1.23456
 * floorToPrecision(0.0000001, 5) // returns 0 (dust removal)
 */
export const floorToPrecision = ( value: number|AmountString, precision = 5 ): number => {
	return z(Math.floor(Number(value) * (10**precision)) / (10**precision), precision);
}



/**
 * Removes dust values by returning 0 for values smaller than the precision threshold
 * @param value - The number or amount string to check for dust
 * @param precision - The precision threshold (default: 5) - values smaller than 1/(10^precision) are considered dust
 * @returns The original number if above dust threshold, otherwise 0
 * @example
 * z(0.0000001, 5) // returns 0 (dust removal)
 * z(0.001, 5) // returns 0.001 (above threshold)
 * z(-0.0000001, 5) // returns 0 (negative dust removal)
 */
export const z = ( value: number|AmountString, precision = 5, ): number => {
	try {
		return Number(value) < (1/(10**precision)) && Number(value) > (1/(10**precision)*-1) ? 0 : Number(value);
	}
	catch(error) {
		console.warn(`client/utils/z hit a rounding error attempting to kill dust of of value [${value}] - ${error instanceof Error ? error.message : "no error message"}`);
		return 0;
	}
}



const subtractAmountStringDefaults: { precision?: number } = { precision: 5, };
